import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  assetCatalog, type CatalogField, type CatalogKind, type CatalogVersionDetail, type CatalogVersionRow,
  type CatalogSlot, type CatalogTemplates, type ModelBody,
} from "@/api/client";
import { useConfirm } from "@/components/ConfirmDialog";
import { Field } from "@/components/Field";
import { ImageField } from "@/components/ImageCapture";
import { Modal } from "@/components/Modal";
import { StatusChip, type StatusMap } from "@/components/StatusChip";
import { Select, SearchInput } from "@/components/inputs";
import { CAP, useSession } from "@/auth/session";
import { useMutationFeedback } from "@/lib/hooks/useMutationFeedback";
import { useStagedImage } from "@/lib/hooks/useStagedImage";

import { ActionRow } from "@/components/ActionRow";
import { Icon } from "@/components/icons";
/* ---------------- vocab ---------------- */
export const MODEL_CHIP: StatusMap = {
  draft: { label: "DRAFT", color: "var(--warn)" },
  active: { label: "ACTIVE", color: "var(--ok)" },
  retired: { label: "RETIRED", color: "var(--t-mute)" },
};
export const ModelChip = ({ st }: { st: string }) => <StatusChip st={st} map={MODEL_CHIP} />;

export const QK = ["asset-catalog"] as const;

/** Templates are static per deploy — fetched once, cached for the session. */
export function useTemplates() {
  return useQuery({ queryKey: [...QK, "templates"], queryFn: assetCatalog.templates, staleTime: Infinity });
}
export function useManufacturers() {
  return useQuery({ queryKey: [...QK, "manufacturers"], queryFn: assetCatalog.manufacturers });
}
/** All non-retired models — the option pool for pickers (catalog-sized: hundreds, not 100K).
 *
 *  `owner` (D-135): only what THAT company may enrol against — visible to it,
 *  of a product it holds, deploys or makes. The enrol pickers pass it, because
 *  a super admin enrolling for Go Electric must see Go Electric's catalogue,
 *  not their own. Omitted = the caller's own view, for the catalogue pages. */
export function useModelPool(owner?: string) {
  return useQuery({ queryKey: [...QK, "models", "pool", owner ?? ""],
    queryFn: () => assetCatalog.models({ limit: "1000", owner: owner || undefined }) });
}
export function kindLabel(t: CatalogTemplates | undefined, id: string) {
  return t?.kinds.find((k) => k.id === id)?.label ?? id;
}

/** Hierarchical kind dropdown (optgroup per family). `only` restricts the pool. */
export function KindSelect({ kinds, value, onChange, only, placeholder = "choose kind", style }: {
  kinds: CatalogKind[]; value: string; onChange: (v: string) => void; only?: string[];
  placeholder?: string; style?: React.CSSProperties;
}) {
  const pool = only ? kinds.filter((k) => only.includes(k.id)) : kinds;
  const groups = Array.from(new Set(pool.map((k) => k.group)));
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} style={style}>
      <option value="">{placeholder}</option>
      {groups.map((g) => (
        <optgroup key={g} label={g}>
          {pool.filter((k) => k.group === g).map((k) => <option key={k.id} value={k.id}>{k.label}{k.abbr ? ` (${k.abbr})` : ""}</option>)}
        </optgroup>
      ))}
    </Select>
  );
}

/** Model dropdown: "name · v2 · Manufacturer (draft)" — retired never offered. */
export function ModelSelect({ rows, value, onChange, placeholder = "choose model", style }: {
  rows: CatalogVersionRow[]; value: string; onChange: (v: string) => void; placeholder?: string;
  style?: React.CSSProperties;
}) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} style={{ minWidth: 0, ...style }}>
      <option value="">{placeholder}</option>
      {rows.map((m) => (
        <option key={m.version_id} value={m.version_id}>
          {m.name} · v{m.version} · {m.manufacturer}{m.status === "draft" ? " (draft)" : ""}
        </option>
      ))}
    </Select>
  );
}


/** Tiny thumbnail for tables / pickers; a dashed box when there is none. */
export function Thumb({ src, size = 28, radius = 6 }: { src: string | null; size?: number; radius?: number }) {
  return src
    ? <img src={src} alt="" style={{ width: size, height: size, objectFit: "cover", borderRadius: radius, border: "1px solid var(--line2)", verticalAlign: "middle", background: "#fff" }} />
    : <span style={{ display: "inline-block", width: size, height: size, borderRadius: radius, border: "1px dashed var(--line2)", verticalAlign: "middle" }} />;
}

const numOf = (v: unknown) => {
  const n = parseFloat(String(v ?? "")); return Number.isFinite(n) ? String(n).replace(".", "p") : "";
};
const vOf = (v: unknown) => String(v ?? "").replace(/\s*V$/i, "").trim();

/** Hover explanation of the naming rule — what is fixed and what is yours. */
export function nameRuleTitle(abbr: string, fam: string, ble: boolean): string {
  return [
    `Naming rule: ${abbr}-${fam}${ble ? "-<rating>-B" : "-<rating>"}-<suffix>`,
    `${abbr} = manufacturer code (fixed, from Manufacturers)`,
    `${fam} = product family (fixed, from Kind - connected kinds have their own code: BMSC, VCUC, CLUC, TCU)`,
    "rating = key specs above (fixed - changes when you edit the specs)",
    `B flag = ${ble ? "present (BLE tick)" : "absent (no BLE tick)"}`,
    "suffix = YOURS to type - hardware revision (R1, R2...); a new PCB spin means a new suffix",
  ].join("\n");
}

/** Technical-name prefix per ASSET_CATALOG.md §2a: ABBR-FAM-RATING-FLAGS-.
 *  Everything is derived from what the form already holds; the user types
 *  only the final segment (hw rev). */
export function namePrefix(kind: string, specs: Record<string, string>, abbr: string, fam?: string): string {
  const parts = [abbr, fam || kind.toUpperCase()];
  // rating: only emit segments whose values are actually present — no
  // dangling unit letters (a cleanup regex here once ate the S of SEG-LCD)
  let rating = "";
  if (kind === "bms" || kind === "bms_c") {
    const [se, a] = [numOf(specs.series_max), numOf(specs.cont_a)];
    rating = se && a ? `${se}S${a}` : se ? `${se}S` : "";
  } else if (kind === "mcu") {
    const [v, a] = [vOf(specs.volt), numOf(specs.cont_a)];
    rating = v && a ? `${v}V${a}` : v ? `${v}V` : "";
  } else if (kind === "dcdc") {
    const [v, w] = [vOf(specs.out_v), numOf(specs.w)];
    rating = v && w ? `${v}V${w}` : v ? `${v}V` : "";
  } else if (kind === "cluster" || kind === "cluster_c") {
    rating = specs.display_type ?? "";          // size stays a spec, not a name segment
  } else if (kind === "cell") {
    rating = `${specs.chem ?? ""}${numOf(specs.ah)}`;
  }
  if (rating) parts.push(rating);
  if (specs.has_ble === "true") parts.push("B");
  return parts.filter(Boolean).join("-") + "-";
}

/* ---------------- manufacturer form (page + modal) ---------------- */
// ManufacturerForm REMOVED 2026-09-29 (D-104). It created a COMPANY from the
// model form, which is a second place for a company to be born — the same
// two-births problem that put "Aphelion Energy Pvt Ltd" in one table and
// "Aphelion Energy" in the other. A manufacturer IS a party (D-80), so it is
// created in Organisations, and ticking a product under its Capabilities is
// what makes it selectable on a model.

type BomRow = { slot: string; child_version_id: string; qty: number; mfr?: string };

function toForm(m?: CatalogVersionDetail | null) {
  const specs: Record<string, string> = {};
  for (const [k, v] of Object.entries(m?.specs ?? {})) specs[k] = v == null ? "" : String(v);
  return {
    manufacturer_id: m?.manufacturer_id ?? "", name: m?.name ?? "", specs,
    bom: (m?.bom ?? []).map((b) => ({ slot: b.slot, child_version_id: b.version_id, qty: b.qty })) as BomRow[],
    version_note: m?.version_note ?? "",
  };
}

/** One form for: new model (kind fixed or chosen from `kinds`), edit draft,
 *  new-version-from-active. Inline "+ New" beside the manufacturer picker and
 *  each BOM slot opens a STACKED modal; the created record is written back
 *  into the opener's dropdown (LIFO: only the top modal closes). */
export function ModelForm({ kinds, model, onDone, onDeleted, onCancel, crumb }: {
  /** allowed kinds; a single entry fixes the kind, several show a picker */
  kinds: string[];
  model?: CatalogVersionDetail | null;
  onDone: (m: CatalogVersionDetail) => void;
  onDeleted?: () => void;
  onCancel?: () => void;
  /** where the result lands — shown in nested modal titles */
  crumb?: string;
}) {
  const qc = useQueryClient();
  // MANAGE_CATALOG is internal-only (plans.py): catalog definitions are
  // platform-level and shared by every company, so one company editing them
  // changes what every other company's assets are built from. Hiding is UX;
  // require_cap on each route is the enforcement.
  const { can } = useSession();
  if (!can(CAP.MANAGE_CATALOG)) {
    return (
      <div className="cap">
        The catalogue is maintained at platform level and is read-only here.
      </div>
    );
  }

  const fb = useMutationFeedback();
  const { ask, dialog } = useConfirm();
  const tpl = useTemplates().data;
  const allMfrs = useManufacturers().data?.manufacturers ?? [];
  const pool = useModelPool().data?.models ?? [];

  const [kind, setKind] = useState(model?.kind ?? (kinds.length === 1 ? kinds[0] : ""));
  const [f, setF] = useState(() => toForm(model));
  useEffect(() => { setF(toForm(model)); setKind(model?.kind ?? (kinds.length === 1 ? kinds[0] : ""));
    setSuffix(model?.name?.includes("-") ? model.name.split("-").pop()! : "R1"); },
    [model?.version_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [suffix, setSuffix] = useState("R1");
  const [newIn, setNewIn] = useState<CatalogSlot | null>(null);   // slot whose "+ New" is open
  // manufacturer chosen for the NEW (not yet added) row of each slot; existing
  // rows carry their own `mfr`, so splices can't cross-contaminate them
  const [newRowMfr, setNewRowMfr] = useState<Record<string, string>>({});
  const staged = useStagedImage();

  // only manufacturers that list THIS kind under Supplies (the one already on
  // the model stays visible even if its Supplies changed later)
  const mfrs = allMfrs.filter((m) => (m.status === "active" && kind && m.supplies.includes(kind)) || m.manufacturer_id === f.manufacturer_id);
  const groups = (kind && tpl?.templates[kind]) || [];
  const slots = (kind && tpl?.slots[kind]) || [];
  const status = model?.status ?? "new";
  const readOnly = status === "retired";
  const invalidate = () => qc.invalidateQueries({ queryKey: [...QK] });

  const mfrAbbr = mfrs.find((m) => m.manufacturer_id === f.manufacturer_id)?.abbr ?? "";
  const kAbbr = tpl?.kinds.find((k) => k.id === kind)?.abbr;
  const nameEditable = !model || model.status === "draft";
  const genName = () => {
    if (!nameEditable) return f.name.trim();
    if (!mfrAbbr || !kind || !suffix.trim()) return "";
    return namePrefix(kind, f.specs, mfrAbbr, kAbbr) + suffix.trim();
  };
  const body = (): ModelBody => ({
    kind, manufacturer_id: f.manufacturer_id, name: genName(),
    specs: Object.fromEntries(Object.entries(f.specs).filter(([, v]) => v !== "")),
    bom: f.bom.filter((b) => b.child_version_id)
      .map(({ slot, child_version_id, qty }) => ({ slot, child_version_id, qty })),
    version_note: f.version_note || null,
  });
  const canSave = !!kind && !!f.manufacturer_id && !!genName();

  // WHO MAY BUILD WITH THIS (D-81). [] = every company.
  //
  // It lives in the FORM, not only in the Overview drawer, because this is
  // where a model is actually made and changed. Being told a control exists on
  // another tab is the same as it not existing.
  //
  // On CREATE the model has no id until it is saved, so the selection waits
  // here and is applied the moment the id exists — the same way the picture
  // rides along below.
  // NO VISIBILITY STATE HERE. It moved to Asset Catalog / Model Visibility
  // (2026-09-28), which also removed the awkward half of it: on CREATE there
  // was no model id yet, so the selection had to ride along and be applied
  // after the save — a second write that could fail on its own, after the
  // form had already reported success. A new model is now visible to every
  // company (the empty default) until somebody decides otherwise, on the page
  // that owns that decision.


  // transient save confirmation — the edit form stays open after Save changes,
  // so without this a successful save is indistinguishable from a dead click
  const [saved, setSaved] = useState(false);
  const savedTimer = useRef<number>();
  const flashSaved = () => {
    setSaved(true);
    window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setSaved(false), 2500);
  };
  useEffect(() => () => window.clearTimeout(savedTimer.current), []);

  const create = useMutation({
    mutationFn: async (activate: boolean) => {
      const m = await assetCatalog.createModel(body());
      // picture rides along once the id exists; a failed upload never fails the create
      if (staged.blob) await assetCatalog.uploadImage(m.version_id, staged.blob).catch(() => undefined);
      // visibility likewise — but this one is NOT swallowed: a model that was
      // meant for two customers and silently landed public is worse than a
      // create that reports it half-finished.
      return activate ? assetCatalog.activate(m.version_id).catch((e) => { throw Object.assign(e, { saved: m }); }) : m;
    },
    onSuccess: (m) => { fb.ok(); flashSaved(); void invalidate(); onDone(m); },
    onError: (e: Error & { saved?: CatalogVersionDetail }) => {
      fb.fail(e);
      // the draft exists even though activation failed — hand it to the opener
      if (e.saved) { void invalidate(); onDone(e.saved); }
    },
  });
  const saveDraft = useMutation({
    mutationFn: async (activate: boolean) => {
      const m = await assetCatalog.updateDraft(model!.version_id, body());
      return activate ? assetCatalog.activate(m.version_id) : m;
    },
    onSuccess: (m) => { fb.ok(); flashSaved(); void invalidate(); onDone(m); },
    // the draft save half may have persisted before activation refused — refresh
    onError: (e) => { fb.fail(e); void invalidate(); },
  });
  const retire = useMutation({
    mutationFn: () => assetCatalog.retire(model!.version_id),
    onSuccess: (m) => { fb.ok(); void invalidate(); onDone(m); }, onError: fb.fail,
  });
  const del = useMutation({
    mutationFn: () => assetCatalog.deleteDraft(model!.version_id),
    onSuccess: () => { fb.ok(); void invalidate(); onDeleted?.(); }, onError: fb.fail,
  });
  const busy = create.isPending || saveDraft.isPending || retire.isPending || del.isPending;

  // ---- derived preview (server is the authority; this mirrors the cheap parts) ----
  const derived = useMemo(() => {
    const d: Record<string, string> = {};
    const v = parseFloat(f.specs.nom_v ?? ""), ah = parseFloat(f.specs.ah ?? "");
    if (kind === "battery" && v > 0 && ah > 0) d.kwh = `${Math.round(v * ah / 10) / 100} kWh`;
    const children = f.bom.map((b) => pool.find((p) => p.version_id === b.child_version_id)).filter(Boolean) as CatalogVersionRow[];
    const cell = children.find((c) => c.kind === "cell");
    if (cell) d.chem = cell.key_specs.split(" · ")[0];
    const dd = (f.specs.ip_dust ?? "").split(" ")[0], dw = (f.specs.ip_water ?? "").split(" ")[0];
    if (dd || dw) d.ip = `IP${dd || "X"}${dw || "X"}`;
    const src = children.filter((c) => tpl?.kinds.find((k) => k.id === c.kind)?.conn);
    if (tpl?.kinds.find((k) => k.id === kind)?.asset) {
      d.connected = src.length === 1 ? "yes" : src.length === 0 ? "no" : "INVALID - two sources";
      if (src.length === 1) d.connected_via = `${kindLabel(tpl, src[0].kind)} ${src[0].name}`;
    }
    return d;
  }, [f, kind, pool, tpl]);
  const derivedText = (fld: CatalogField) => {
    const v = derived[fld.k] ?? (model?.derived?.[fld.k] as string | number | undefined);
    return v == null ? "—" : String(v);
  };

  const setSpec = (k: string, v: string) => setF((p) => {
    const specs = { ...p.specs, [k]: v };
    // unticking BLE takes its dependent FOTA options with it (mirror of the
    // server rule in clean_specs) — a hidden stale tick must never be sent
    if (k === "has_ble" && v !== "true") {
      delete specs.fota_host_ble; delete specs.fota_ble_cell; delete specs.fota_ble_phone;
    }
    return { ...p, specs };
  });
  const setBom = (rows: BomRow[]) => setF((p) => ({ ...p, bom: rows }));

  const rowGrid: React.CSSProperties = {
    display: "grid", gridTemplateColumns: "minmax(0,220px) minmax(0,1fr) 70px", gap: 10,
    alignItems: "center", padding: "7px 0", borderBottom: "1px solid var(--line)",
  };
  const lbl = (fld: CatalogField) => (
    <span title={fld.help} style={{ fontSize: 12.5, color: "var(--t-mute)" }}>
      {fld.label}{fld.req && <span style={{ color: "var(--orange)" }}> *</span>}
      {fld.k.startsWith("fota_") && fld.help && (
        <div className="mono" style={{ fontSize: 11, color: "var(--t-dim)" }}>{fld.help}</div>
      )}
    </span>
  );
  const input = (fld: CatalogField) => {
    const v = f.specs[fld.k] ?? "";
    if (fld.type === "derived") {
      return <span className="mono" style={{ fontSize: 12.5, color: "var(--t-bright)" }}>{derivedText(fld)}
        <span style={{ color: "var(--t-dim)", fontSize: 11 }}> · derived{fld.from ? ` from ${fld.from}` : ""}</span></span>;
    }
    if (fld.type === "bool") {
      return (
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, cursor: readOnly ? "default" : "pointer", height: 30 }}>
          <input type="checkbox" disabled={readOnly} checked={v === "true"}
            onChange={(e) => setSpec(fld.k, e.target.checked ? "true" : "")} />
          <span className="muted">{v === "true" ? "yes" : "no"}</span>
        </label>
      );
    }
    if (fld.type === "select") {
      return <Select className="fill" value={v} disabled={readOnly} onChange={(e) => setSpec(fld.k, e.target.value)}>
        <option value="">—</option>{fld.opts?.map((o) => <option key={o} value={o}>{o}</option>)}</Select>;
    }
    return <SearchInput className="fill" value={v} disabled={readOnly} type={fld.type === "num" ? "number" : "text"} mono={fld.type === "num"}
      step={fld.type === "num" ? (fld.step ?? "any") : undefined} min={fld.type === "num" ? 0 : undefined}
      placeholder={fld.ph} onChange={(e) => setSpec(fld.k, e.target.value)} />;
  };

  return (
    <div>
      {/* identity */}
      <div className="section-label">Identity</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14, marginBottom: 18 }}>
        {kinds.length > 1 && (
          <Field label="Kind" req>
            <KindSelect kinds={tpl?.kinds ?? []} only={kinds} value={kind}
              onChange={(k) => { setKind(k); setF((p) => ({ ...p, specs: {}, bom: [] })); }}
              style={{ width: "100%", minWidth: 0 }} />
          </Field>
        )}
        <Field label="Manufacturer" req>
          <div style={{ display: "flex", gap: 6 }}>
            <Select value={f.manufacturer_id} disabled={readOnly || !kind} onChange={(e) => setF((p) => ({ ...p, manufacturer_id: e.target.value }))}
              style={{ flex: 1, minWidth: 0 }}>
              <option value="">{!kind ? "choose kind first" : mfrs.length ? "choose" : ` - none supplies ${kindLabel(tpl, kind)} - add one - `}</option>
              {mfrs.map((m) => <option key={m.manufacturer_id} value={m.manufacturer_id}>{m.abbr ? `${m.abbr} - ${m.name}` : m.name}</option>)}
            </Select>
            {/* NO "+ NEW" HERE ANY MORE (D-104). Dattatray: *"we fetch this
                list while adding a product model and remove the add
                manufacturer button and implementation from the product models
                page."*

                A manufacturer IS a party (D-80), so creating one from this
                form created a company from a second place - the same
                two-births problem that put "Aphelion Energy Pvt Ltd" in one
                table and "Aphelion Energy" in the other (SS9k). A company is
                born in Organisations, and ticking a product under its
                Capabilities is what makes it selectable here. */}
          </div>
        </Field>
        {model && (
          <Field label="Version">
            <div style={{ display: "flex", gap: 8, alignItems: "center", height: 34 }}>
              <span className="mono">v{model.version}</span><ModelChip st={model.status} />
            </div>
          </Field>
        )}
      </div>

      {/* the technical name gets a full-width row - the prefix is long */}
      <div style={{ marginTop: 14, marginBottom: 18 }}>
        {(() => {
          const prefix = mfrAbbr && kind ? namePrefix(kind, f.specs, mfrAbbr, kAbbr) : "";
          const ruleTitle = prefix ? nameRuleTitle(mfrAbbr, kAbbr ?? kind.toUpperCase(), f.specs.has_ble === "true") : undefined;
          if (!nameEditable) {
            return (
              <Field label="Model name (fixed - versions are immutable)">
                <span className="mono" style={{ fontSize: 12.5, color: "var(--t-bright)", display: "inline-block",
                  background: "var(--surface)", border: "1px solid var(--line2)", borderRadius: 8, padding: "7px 12px" }}>{f.name}</span>
              </Field>
            );
          }
          if (!prefix) {
            return (
              <Field label="Model name (generated)" req>
                <span style={{ fontSize: 12.5, color: "var(--t-mute)" }}>
                  Choose the kind and a manufacturer first - the name is assembled from them, the specs and the ticks;
                  you type only the final segment. A manufacturer without an abbreviation must get one on the Manufacturers page.
                </span>
              </Field>
            );
          }
          return (
            <Field label="Model name (generated - you type only the last segment)" req>
              <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
                <span className="mono" title={ruleTitle}
                  style={{ fontSize: 12.5, color: "var(--t-bright)", background: "var(--surface)", border: "1px solid var(--line2)",
                    borderRadius: 8, padding: "8px 14px", whiteSpace: "nowrap", minWidth: 220, textAlign: "left" }}>{prefix}</span>
                <SearchInput mono value={suffix} placeholder="R1" maxLength={12}
                  style={{ width: 180, minWidth: 0 }}
                  title="the one segment you type: hardware revision (R1, R2...)" onChange={(e) => setSuffix(e.target.value)} />
                <span className="mono" style={{ fontSize: 12, color: "var(--t-mute)", whiteSpace: "nowrap" }}>= {prefix}{suffix || "…"}</span>
              </div>
            </Field>
          );
        })()}
      </div>

      {!kind && <div className="cap">Choose a kind to load its specification template.</div>}

      {/* specs, row-wise per group. FOTA rows appear only when the model's
          facts allow them (connected kind, BLE tick) - never disabled/N-A.

          THE TWO `"FOTA support"` BRANCHES BELOW ARE LEGACY-ONLY AFTER 0102
          (D-123), and they are kept rather than deleted. 0102 swaps that group
          for `"Software"` in every stored spec that has it, so on a migrated
          database neither branch can match - but `catalog_kinds.spec` is DATA,
          and a database that has not run 0102 still serves the flat group. The
          branches are what renders it correctly there.

          DELETE THEM when no stored spec carries the group any more:
            SELECT kind FROM catalog_kinds
             WHERE spec @> '[{"group":"FOTA support"}]';
          Zero rows on every environment is the condition, not "0102 has run
          here". */}
      {groups.map((g) => {
        const hasBle = f.specs.has_ble === "true";
        const fields = g.group !== "FOTA support" ? g.fields : g.fields.filter((fld) =>
          !["fota_host_ble", "fota_ble_cell", "fota_ble_phone"].includes(fld.k) || hasBle);
        if (fields.length === 0) return null;
        return (
        <div key={g.group} style={{ marginBottom: 16 }}>
          <div className="section-label">{g.group}</div>
          <div className="panel" style={{ padding: "2px 14px" }}>
            {fields.map((fld) => (
              /* A SOFTWARE TREE IS NOT A LABEL/CONTROL ROW. Every other field
                 renders into the 220px-label / control / unit grid; a tree is
                 three nested levels of ticks and has to own the full width.
                 Rendered INSTEAD of the row rather than inside it, because a
                 tree squeezed into the control column is the unusable shape
                 this field type exists to avoid. */
              fld.type === "swtree" ? (
                <SwTree key={fld.k} fld={fld} specs={f.specs} hasBle={hasBle}
                        readOnly={readOnly} setSpec={setSpec} />
              ) : (
              <div key={fld.k} style={rowGrid}>
                {lbl(fld)}
                {input(fld)}
                <span className="mono" style={{ fontSize: 11.5, color: "var(--t-dim)" }}>{fld.unit ?? ""}</span>
              </div>
              )
            ))}
          </div>
          {g.group === "FOTA support" && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, fontSize: 12, color: "var(--t-mute)" }}>
              FOTA (derived):&nbsp;
              {([["fota_host_cell", "HOST · CELL"], ["fota_host_ble", "HOST · PHONE"], ["fota_modem_cell", "TEL MODEM"],
                 ["fota_ble_cell", "BLE · CELL"], ["fota_ble_phone", "BLE · PHONE"]] as const)
                .filter(([k2]) => f.specs[k2] === "true")
                .map(([k2, label]) => <StatusChip key={k2} st="x" map={{ x: { label, color: "var(--teal)" } }} />)}
              {!["fota_host_cell", "fota_host_ble", "fota_modem_cell", "fota_ble_cell", "fota_ble_phone"].some((k2) => f.specs[k2] === "true") &&
                <StatusChip st="x" map={{ x: { label: "NONE", color: "var(--t-mute)" } }} />}
            </div>
          )}
        </div>
        );
      })}

      {/* bill of materials */}
      {slots.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div className="section-label">Bill of materials</div>
          <div className="panel" style={{ padding: "2px 14px" }}>
            {slots.map((sl) => {
              const rows = f.bom.filter((b) => b.slot === sl.slot);
              // retired models are never offered — but one already IN the BOM must stay
              // visible, or the dropdown would render blank for a real value
              const opts = pool.filter((p) => sl.allow.includes(p.kind) &&
                (p.status !== "retired" || rows.some((r) => r.child_version_id === p.version_id)));
              const setRow = (i: number, patch: Partial<BomRow>) => {
                const all = [...f.bom]; let n = -1;
                for (let j = 0; j < all.length; j++) if (all[j].slot === sl.slot && ++n === i) { all[j] = { ...all[j], ...patch }; break; }
                setBom(all);
              };
              const removeRow = (i: number) => {
                const all = [...f.bom]; let n = -1;
                for (let j = 0; j < all.length; j++) if (all[j].slot === sl.slot && ++n === i) { all.splice(j, 1); break; }
                setBom(all);
              };
              // manufacturer FIRST, then that maker's models — a flat list of
              // every model in the catalog stops being usable at real counts
              const makers = [...new Set(opts.map((p) => p.manufacturer))].sort();
              const line = (b: BomRow | null, i: number) => {
                const fromModel = b?.child_version_id
                  ? (pool.find((p) => p.version_id === b.child_version_id)?.manufacturer ?? "") : "";
                const mfr = fromModel || (b ? b.mfr ?? "" : newRowMfr[sl.slot] ?? "");
                return (
                <div key={b?.child_version_id || `new-${sl.slot}-${i}`}
                  style={{ display: "flex", gap: 6, alignItems: "center", minWidth: 0 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.25fr)",
                    gap: 6, flex: 1, minWidth: 0 }}>
                    <Select value={mfr} style={{ minWidth: 0 }}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (b) setRow(i, { child_version_id: "", mfr: v });   // maker changed → pick again
                        else setNewRowMfr((prev) => ({ ...prev, [sl.slot]: v }));
                      }}>
                      <option value="">{makers.length ? "manufacturer" : "none in catalog"}</option>
                      {makers.map((m) => <option key={m} value={m}>{m}</option>)}
                    </Select>
                    <ModelSelect rows={opts.filter((p) => p.manufacturer === mfr)}
                      value={b?.child_version_id ?? ""}
                      placeholder={mfr ? "model" : "manufacturer first"}
                      onChange={(v) => b ? setRow(i, { child_version_id: v })
                        : v && setBom([...f.bom, { slot: sl.slot, child_version_id: v, qty: 1, mfr }])} />
                  </div>
                  {sl.qty && b && (
                    <SearchInput type="number" mono value={b.qty} min={1} title="quantity" style={{ width: 70, minWidth: 0 }}
                      onChange={(e) => setRow(i, { qty: Math.max(1, parseInt(e.target.value || "1", 10)) })} />
                  )}
                  {b && !readOnly && <button className="btn red" onClick={() => removeRow(i)} title="Remove"><Icon name="close" /></button>}
                  {!b && !readOnly && <button className="btn ghost" onClick={() => setNewIn(sl)} title="Create a model for this slot">+ New</button>}
                </div>
              );
              };
              return (
                <div key={sl.slot} style={rowGrid}>
                  <span title={sl.help} style={{ fontSize: 12.5, color: "var(--t-mute)" }}>
                    {sl.role}{sl.min > 0 && <span style={{ color: "var(--orange)" }}> *</span>}
                    {sl.help && <div style={{ fontSize: 11, color: "var(--t-dim)" }}>{sl.help}</div>}
                  </span>
                  <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                    {rows.map((b, i) => line(b, i))}
                    {rows.length < sl.max && !readOnly && line(null, rows.length)}
                  </div>
                  <span className="mono" style={{ fontSize: 11.5, color: "var(--t-dim)" }}>{sl.min}–{sl.max}</span>
                </div>
              );
            })}
          </div>
          {derived.connected === "INVALID - two sources" && (
            <div style={{ color: "var(--bad)", fontSize: 12, marginTop: 6 }}>
              Two connectivity sources - an asset may have at most one connected part OR a TCU. Activation will refuse this.
            </div>
          )}
        </div>
      )}

      {/* picture - not a spec: allowed on any status, carried into new versions */}
      {kind && (
        <div style={{ marginBottom: 16 }}>
          <div className="section-label">Picture</div>
          {model ? (
            <ImageField size={120}
              src={model.image ? assetCatalog.imageUrl(model.version_id, model.image.captured_at) : null}
              meta={model.image ? `${new Date(model.image.captured_at).toLocaleDateString()} · ${model.image.captured_by}` : undefined}
              onUpload={async (b) => { await assetCatalog.uploadImage(model.version_id, b); await invalidate(); }}
              onRemove={readOnly ? undefined : async () => { await assetCatalog.deleteImage(model.version_id); await invalidate(); }} />
          ) : (
            <ImageField size={120} src={staged.url} meta="attached - saved with the model"
              onUpload={async (b) => staged.set(b)} onRemove={async () => staged.set(null)} />
          )}
        </div>
      )}

      {/* version note + actions */}
      {kind && !readOnly && (
        <Field label="Version note">
          <SearchInput className="fill" value={f.version_note} placeholder="e.g. switched to LF32 cells; +2 Ah"
            onChange={(e) => setF((p) => ({ ...p, version_note: e.target.value }))} />
        </Field>
      )}
      {/* VISIBILITY IS NOT HERE ANY MORE (2026-09-28). It was the last
          section of this form, and Dattatray moved it out: *"I think this
          needs a separate sub page in the asset catalog instead of being a
          selection in the Product models."*

          The form describes what a model IS - voltage, chemistry, what is
          fitted inside it. Visibility describes who may USE it: a commercial
          decision, taken later, by somebody who is not editing the design,
          and sometimes on a version that is retired and read-only here. It
          lives on Asset Catalog / Model Visibility, and the Overview's
          "Visible to" button opens that page with the model already chosen. */}

      <ActionRow top={18}>
        {saved && <span className="chip ok">Saved ✓</span>}
        {fb.errorNode}
        {onCancel && <button className="btn ghost" onClick={onCancel}>Cancel</button>}
        {status === "new" && <>
          <button className="btn ghost" disabled={!canSave || busy} onClick={() => create.mutate(false)}>Save draft</button>
          <button className="btn teal" disabled={!canSave || busy} onClick={() => create.mutate(true)}>Save &amp; activate</button>
        </>}
        {status === "draft" && <>
          <button className="btn red" disabled={busy}
            onClick={() => ask(`Delete draft ${model!.name} v${model!.version}? This cannot be undone.`, () => del.mutate(), "Delete draft")}>Delete draft</button>
          <button className="btn ghost" disabled={!canSave || busy} onClick={() => saveDraft.mutate(false)}>Save changes</button>
          <button className="btn teal" disabled={!canSave || busy} onClick={() => saveDraft.mutate(true)}>Save &amp; activate</button>
        </>}
        {status === "active" && <>
          <button className="btn red" disabled={busy}
            onClick={() => ask(`Retire ${model!.name} v${model!.version}? Existing units keep pointing at it; no new units can be built on it.`, () => retire.mutate(), "Retire")}>Retire</button>
          <button className="btn teal" disabled={!canSave || busy} onClick={() => saveDraft.mutate(false)}>Save changes</button>
        </>}
        {readOnly && <span className="cap">Retired versions are read-only - open the active version of this line to change it.</span>}
      </ActionRow>
      {dialog}

      {newIn && (
        <Modal wide title={`New ${newIn.allow.length === 1 ? kindLabel(tpl, newIn.allow[0]) : newIn.role.toLowerCase()}`}
          crumb={`→ ${crumb ?? "this model"} · ${newIn.role}`} onClose={() => setNewIn(null)}>
          <ModelForm kinds={newIn.allow} crumb={`${crumb ?? (f.name || "this model")} · ${newIn.role}`}
            onCancel={() => setNewIn(null)}
            onDone={(m) => {
              // write-back: land the created model in the slot that opened the pop-up
              setBom([...f.bom, { slot: newIn.slot, child_version_id: m.version_id, qty: 1 }]);
              setNewIn(null);
            }} />
        </Modal>
      )}
    </div>
  );
}

/** Specs rendered read-only by template group (drawer). */
export function SpecsView({ tpl, m }: { tpl: CatalogTemplates | undefined; m: CatalogVersionDetail }) {
  const groups = tpl?.templates[m.kind] ?? [];
  const val = (fld: CatalogField): ReactNode => {
    const v = fld.type === "derived" ? m.derived?.[fld.k] : m.specs[fld.k];
    if (v == null || v === "") return <span style={{ color: "var(--t-dim)" }}>—</span>;
    return <>{String(v)}{fld.unit && fld.unit !== "n" ? ` ${fld.unit}` : ""}</>;
  };
  return (
    <>
      {groups.map((g) => (
        <div key={g.group} style={{ marginTop: 14 }}>
          <div className="section-label">{g.group}</div>
          {g.fields.map((fld) => (
            /* A SOFTWARE TREE HAS NO VALUE OF ITS OWN, so the label/value row
               cannot render it: `clean_specs` pops `sw_tree` and stores one key
               per tick instead. Sent through the row anyway, this printed the
               field's label against a single em-dash - the whole software story
               of a product reading as "not set". */
            fld.type === "swtree"
              ? <SwTreeRead key={fld.k} fld={fld} specs={m.specs} />
              : (
            <div key={fld.k} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12.5, padding: "5px 0", borderBottom: "1px solid var(--line)" }}>
              <span className="muted">{fld.label}</span>
              <span className="mono" style={{ color: "var(--t-bright)", textAlign: "right" }}>{val(fld)}</span>
            </div>
              )
          ))}
        </div>
      ))}
    </>
  );
}

/** The three-level software declaration: nodes, their components, the routes
 *  to each (D-123).
 *
 *  Dattatray: *"Saperate - SW enabled Nodes in the Model (Host, BLE etc) -
 *  What kind of SW componentts each node (app SW, bootlader SW, Configuration
 *  etc..) - Routes to for SW compoenent to reach the product!"*
 *
 *  PROGRESSIVE, WHICH IS THE WHOLE POINT. A node's components appear only once
 *  the node is ticked, and a component's routes only once it is. The flat
 *  alternative is twenty-seven checkboxes of which seven matter, which is the
 *  form he refused -- *"i dont want 9 rows today"*.
 *
 *  UNTICKING CASCADES, mirroring `clean_specs` on the server. Unticking a node
 *  clears its components and their routes; unticking a component clears its
 *  routes. Without this a hidden stale tick stays in `specs` and is REFUSED on
 *  save by the hierarchy rule -- an error about a control the operator cannot
 *  see. The server refusal is still the rule; this keeps the form from
 *  tripping it.
 */
function SwTree({ fld, specs, hasBle, readOnly, setSpec }: {
  fld: CatalogField;
  specs: Record<string, string>;
  hasBle: boolean;
  readOnly?: boolean;
  setSpec: (k: string, v: string) => void;
}) {
  const on = (k: string) => specs[k] === "true";
  const tick = (k: string, v: boolean, clears: string[] = []) => {
    setSpec(k, v ? "true" : "");
    if (!v) clears.forEach((c) => setSpec(c, ""));
  };

  // No `k` parameter: the key is not rendered anywhere (no htmlFor, the label
  // wraps its own input), and an unused argument at three call sites is three
  // chances to pass the wrong one.
  const box = (label: string, checked: boolean, disabled: boolean,
               title: string | undefined, onChange: (v: boolean) => void) => (
    <label title={title} style={{ display: "inline-flex", alignItems: "center", gap: 7,
      fontSize: 12.5, cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.45 : 1 }}>
      <input type="checkbox" checked={checked} disabled={disabled}
        onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );

  return (
    <div style={{ padding: "12px 0" }}>
      <div style={{ fontSize: 11.5, color: "var(--t-dim)", lineHeight: 1.4, marginBottom: 12 }}>
        {fld.help}
      </div>
      <div style={{ display: "grid", gap: 10 }}>
        {(fld.nodes ?? []).map((node) => {
          // A node whose hardware is absent cannot be declared. The server
          // refuses it too; this is the courtesy half.
          const blocked = !!node.needs_ble && !hasBle;
          const nodeOn = on(node.k) && !blocked;
          const allKids = node.components.flatMap((c) => [c.k, ...c.routes.map((r) => r.k)]);
          return (
            <div key={node.k} style={{ borderLeft: "2px solid var(--line2)", paddingLeft: 12 }}>
              {box(node.label, nodeOn, !!readOnly || blocked,
                   blocked ? "tick “BLE module fitted” first: this node IS the BLE module"
                           : undefined,
                   (v) => tick(node.k, v, allKids))}
              {nodeOn && (
                <div style={{ display: "grid", gap: 8, marginTop: 8, marginLeft: 22 }}>
                  {node.components.map((comp) => {
                    const compOn = on(comp.k);
                    return (
                      <div key={comp.k}>
                        {box(comp.label, compOn, !!readOnly, undefined,
                             (v) => tick(comp.k, v, comp.routes.map((r) => r.k)))}
                        {compOn && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 14,
                            marginTop: 6, marginLeft: 22 }}>
                            {comp.routes.map((r) => box(
                              r.label, on(r.k), !!readOnly, r.help,
                              (v) => tick(r.k, v)))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The software tree, READ-ONLY (D-123) - the drawer's counterpart to `SwTree`.
 *
 *  SHOWS ONLY WHAT IS DECLARED, which is the opposite of the editable form. The
 *  form must offer every node so one can be ticked; a reader wants the four
 *  lines that are true, not nine rows of blanks. An undeclared node is absent
 *  rather than shown as "no", because the form never asked about most of them.
 *
 *  READS THE SAME KEYS `clean_specs` WRITES, through the field's own `nodes`
 *  structure rather than by rebuilding key names here. Rebuilding them is how a
 *  near-miss (`sw_host_bootloader` vs `sw_host_boot`) renders an empty tree
 *  against specs that are perfectly well formed.
 */
function SwTreeRead({ fld, specs }: { fld: CatalogField; specs: Record<string, unknown> }) {
  // `Record<string, unknown>`, NOT `Record<string, string>`, and the difference
  // is a bug if you get it wrong. `ModelForm` stringifies every spec on load
  // (`String(v)`), so inside the editable form a tick is the STRING "true";
  // `SpecsView` reads `m.specs` straight off the wire, where `clean_specs` wrote
  // a real JSONB boolean. So `=== "true"` alone renders an empty tree against
  // perfectly good data, and only in the drawer.
  const on = (k: string) => String(specs[k]) === "true";

  const declared = (fld.nodes ?? [])
    .map((node) => ({
      label: node.label,
      comps: node.components
        .filter((c) => on(c.k) || c.routes.some((r) => on(r.k)))
        .map((c) => ({ label: c.label, routes: c.routes.filter((r) => on(r.k)) })),
      // A node can be ticked with nothing under it yet - a real, saveable state
      // (the hierarchy rule only forbids the reverse), so it is reported rather
      // than filtered out as if the node were not declared.
      nodeOn: on(node.k),
    }))
    .filter((n) => n.nodeOn || n.comps.length > 0);

  if (declared.length === 0) {
    return (
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12.5, padding: "5px 0", borderBottom: "1px solid var(--line)" }}>
        <span className="muted">{fld.label}</span>
        <span className="mono" style={{ color: "var(--t-dim)" }}>none declared</span>
      </div>
    );
  }

  return (
    <div style={{ padding: "6px 0", borderBottom: "1px solid var(--line)" }}>
      {declared.map((n) => (
        <div key={n.label} style={{ fontSize: 12.5, padding: "3px 0" }}>
          <span style={{ color: "var(--t-bright)" }}>{n.label}</span>
          {n.comps.length === 0
            ? <span style={{ color: "var(--t-dim)" }}>{" "}(no software declared yet)</span>
            : (
            <div style={{ marginLeft: 14, marginTop: 2 }}>
              {n.comps.map((c) => (
                <div key={c.label} className="muted" style={{ padding: "1px 0" }}>
                  {c.label}
                  {c.routes.length > 0 && (
                    <span className="mono" style={{ color: "var(--t-mute)", fontSize: 11.5 }}>
                      {"  "}{c.routes.map((r) => r.label).join(" · ")}
                    </span>
                  )}
                  {/* A component with no route is declared but UNDELIVERABLE.
                      Named, because it is the state that produces "the bench
                      was never offered the file" and it is invisible
                      otherwise. */}
                  {c.routes.length === 0 && (
                    <span style={{ color: "var(--t-dim)", fontSize: 11.5 }}>{"  "}no route declared</span>
                  )}
                </div>
              ))}
            </div>
            )}
        </div>
      ))}
    </div>
  );
}
